import { useState, useEffect } from "react";
import { FaTimes, FaBars } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import image1 from "../assets/fastrupeeslogo.png";
import { useTheme } from "../context/ThemeContext";
import { trackEvent } from "../services/firebase";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Product", href: "/products" },
  { label: "Loan Calculator", href: "/loan-calculator" },
  { label: "Blogs", href: "/blogs" },
  { label: "Contact Us", href: "/contact" },
];

const BRAND_PURPLE = "#5c168a";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const activePath = location.pathname;
  const { isDark } = useTheme();
  const appLink = import.meta.env.VITE_WEBSITE_LINK;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navStyle = {
    background: isDark ? 'rgba(10,10,10,0.95)' : (scrolled ? 'rgba(255,255,255,0.95)' : '#FFFFFF'),
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    borderBottom: `1px solid ${isDark ? '#2A2A2A' : '#E2E8F0'}`,
    boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
  };

  const linkColor = (isActive: boolean) =>
    isActive ? BRAND_PURPLE : (isDark ? '#cccccc' : '#475569');

  const ctas = [
    { label: "Login", href: `${appLink}/login`, event: "login_click", filled: false },
    { label: "Repay Now", href: `${appLink}/repay-now`, event: "repay_now_click", filled: false },
    { label: "Apply Now", href: appLink, event: "apply_now_click", filled: true },
  ];

  return (
    <nav
      className="w-full fixed top-0 left-0 z-50 transition-all duration-300"
      style={{ animation: 'slideDown 0.8s ease-out', fontFamily: "'Geist', sans-serif", ...navStyle }}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 xl:px-10 min-[1440px]:px-[80px] h-[80px] xl:h-[100px] flex items-center justify-between">
        <a href="/" className="relative block shrink-0 h-[70px] w-[143px] xl:h-[92px] xl:w-[188px] overflow-hidden">
          <img
            className="absolute max-w-none h-[194.27%] w-[142.79%] left-[-24.39%] top-[-38.99%]"
            src={image1}
            alt={`${import.meta.env.VITE_COMPANY_NAME}`}
          />
        </a>

        <div className="hidden xl:flex items-center gap-10 ml-10 min-[1440px]:ml-[90px] mr-auto">
          {NAV_LINKS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="relative font-semibold text-[14px] uppercase whitespace-nowrap transition-colors duration-200 hover:text-[#5c168a]"
              style={{ color: linkColor(activePath === item.href) }}
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="hidden xl:flex items-center gap-2">
          {ctas.map((cta) => (
            <a
              key={cta.label}
              href={cta.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent(cta.event, { location: "navbar_desktop" })}
              className={`inline-flex items-center justify-center px-6 py-3 rounded-[45px] font-bold text-[14px] uppercase whitespace-nowrap transition-opacity hover:opacity-90 ${
                cta.filled ? 'bg-[#5c168a] text-white' : 'border border-[#5c168a] text-[#5c168a]'
              }`}
            >
              {cta.label}
            </a>
          ))}
        </div>

        <button
          className="xl:hidden text-2xl transition-all duration-300 hover:scale-110"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          style={{ color: isDark ? '#cccccc' : '#475569' }}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      <div
        className={`xl:hidden overflow-hidden transition-all duration-500 ease-in-out ${menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}
        style={{ backgroundColor: isDark ? 'rgba(10,10,10,0.98)' : 'rgba(255,255,255,0.98)' }}
      >
        <div className="flex flex-col items-center gap-3 py-6 px-4">
          {NAV_LINKS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setMenuOpen(false)}
              className="w-full text-center px-6 py-3 rounded-xl font-semibold text-[14px] uppercase transition-colors duration-200"
              style={{
                color: linkColor(activePath === item.href),
                border: `1px solid ${activePath === item.href ? BRAND_PURPLE : (isDark ? '#333' : '#E2E8F0')}`,
              }}
            >
              {item.label}
            </a>
          ))}

          {ctas.map((cta) => (
            <a
              key={cta.label}
              href={cta.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent(cta.event, { location: "navbar_mobile" })}
              className={`w-full text-center px-6 py-3 rounded-[45px] font-bold text-[14px] uppercase ${
                cta.filled ? 'bg-[#5c168a] text-white' : 'border border-[#5c168a] text-[#5c168a]'
              }`}
            >
              {cta.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
